/**
 * Pet breed matching service
 * Uses The Dog API and The Cat API to fetch breed information
 * and match breeds based on user quiz responses
 * Falls back to demo data when APIs are unavailable
 */

import { demoDogBreeds, demoCatBreeds, demoImages } from '../data/demoBreeds';

// API endpoints
const DOG_API_BASE = 'https://api.thedogapi.com/v1';
const CAT_API_BASE = 'https://api.thecatapi.com/v1';

// API keys (these would typically be in environment variables)
// For public demo, using free tier without keys initially
const DOG_API_KEY = import.meta.env?.VITE_DOG_API_KEY || '';
const CAT_API_KEY = import.meta.env?.VITE_CAT_API_KEY || '';

// Common adoptable dog breeds (found frequently in shelters)
const COMMON_DOG_BREEDS = [
  'labrador retriever', 'golden retriever', 'german shepherd', 'bulldog', 
  'beagle', 'rottweiler', 'german shorthaired pointer', 'yorkshire terrier',
  'boxer', 'siberian husky', 'boston terrier', 'american pit bull terrier',
  'chihuahua', 'shih tzu', 'poodle', 'australian shepherd', 'border collie',
  'dachshund', 'cocker spaniel', 'great dane', 'mastiff', 'jack russell terrier',
  'pug', 'weimaraner', 'rhodesian ridgeback', 'australian cattle dog',
  'american staffordshire terrier', 'staffordshire bull terrier'
];

// Common adoptable cat breeds (found frequently in shelters)
const COMMON_CAT_BREEDS = [
  'domestic shorthair', 'domestic longhair', 'siamese', 'maine coon', 
  'persian', 'ragdoll', 'american shorthair', 'british shorthair',
  'abyssinian', 'russian blue', 'bengal', 'birman', 'norwegian forest cat',
  'scottish fold', 'oriental', 'exotic shorthair', 'manx', 'devon rex',
  'american curl', 'tonkinese', 'burmese', 'bombay', 'cornish rex'
];

// Helper function to check if a breed name matches common breeds
const isCommonBreed = (breedName, type) => {
  const name = breedName.toLowerCase();
  const commonList = type === 'dog' ? COMMON_DOG_BREEDS : COMMON_CAT_BREEDS;
  
  // Direct match or partial match
  return commonList.some(common => {
    return name.includes(common) || 
           common.includes(name) ||
           // Handle common variations
           (name.includes('pit') && common.includes('pit bull')) ||
           (name.includes('husky') && common.includes('siberian husky')) ||
           (name.includes('shepherd') && common.includes('german shepherd')) ||
           (name.includes('retriever') && (common.includes('labrador') || common.includes('golden'))) ||
           (name.includes('mixed') || name.includes('mix')) // Mixed breeds are very common in shelters
  });
};

/**
 * Fetch dog breeds from The Dog API with demo fallback
 */
export const fetchDogBreeds = async () => {
  try {
    const headers = DOG_API_KEY ? { 'x-api-key': DOG_API_KEY } : {};
    const response = await fetch(`${DOG_API_BASE}/breeds`, { headers });
    
    if (!response.ok) {
      throw new Error(`Dog API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching dog breeds, using demo data:', error);
    return demoDogBreeds;
  }
};

/**
 * Fetch cat breeds from The Cat API with demo fallback
 */
export const fetchCatBreeds = async () => {
  try {
    const headers = CAT_API_KEY ? { 'x-api-key': CAT_API_KEY } : {};
    const response = await fetch(`${CAT_API_BASE}/breeds`, { headers });
    
    if (!response.ok) {
      throw new Error(`Cat API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching cat breeds, using demo data:', error);
    return demoCatBreeds;
  }
};

/**
 * Fetch breed image from API
 */
export const fetchBreedImage = async (breedId, type = 'dog') => {
  try {
    const baseUrl = type === 'dog' ? DOG_API_BASE : CAT_API_BASE;
    const apiKey = type === 'dog' ? DOG_API_KEY : CAT_API_KEY;
    const headers = apiKey ? { 'x-api-key': apiKey } : {};
    
    const response = await fetch(
      `${baseUrl}/images/search?breed_ids=${breedId}&limit=1`,
      { headers }
    );
    
    if (!response.ok) {
      throw new Error(`${type} image API error: ${response.status}`);
    }
    
    const images = await response.json();
    return images.length > 0 ? images[0].url : null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error fetching ${type} image:`, error);
    return null;
  }
};

/**
 * Score a breed based on user preferences
 */
const scoreBreed = (breed, userPreferences, type) => {
  let score = 0;
  const { experience, living, time, size, energy } = userPreferences;

  // Commonality bonus - prioritize breeds commonly found in shelters
  if (isCommonBreed(breed.name, type)) {
    score += 25; // Significant bonus for common adoptable breeds
  }

  // Experience level scoring
  if (experience === 'first-time') {
    // Prefer easier breeds for first-time owners
    if (type === 'dog') {
      const easyTraits = breed.bred_for?.toLowerCase();
      if (easyTraits?.includes('companion') || easyTraits?.includes('lap')) score += 20;
      if (breed.temperament?.toLowerCase().includes('gentle')) score += 15;
      if (breed.temperament?.toLowerCase().includes('friendly')) score += 10;
    } else {
      // Cats - prefer calm, affectionate breeds
      if (breed.temperament?.toLowerCase().includes('gentle')) score += 15;
      if (breed.temperament?.toLowerCase().includes('calm')) score += 15;
      if (breed.affection_level >= 4) score += 10;
    }
  } else if (experience === 'experienced') {
    // Experienced owners can handle more challenging breeds
    if (type === 'dog' && breed.temperament?.toLowerCase().includes('intelligent')) score += 15;
    if (type === 'cat' && breed.intelligence >= 4) score += 15;
  }

  // Living situation scoring
  if (living === 'apartment') {
    if (type === 'dog') {
      // Smaller, less active dogs for apartments
      if (breed.height?.metric) {
        const avgHeight = parseFloat(breed.height.metric.split(' - ')[0]) || 30;
        if (avgHeight < 35) score += 20; // Smaller breeds
      }
      if (breed.temperament?.toLowerCase().includes('quiet')) score += 15;
    } else {
      // Most cats are fine in apartments
      score += 10;
      if (breed.energy_level <= 3) score += 10;
    }
  } else if (living === 'house-large' || living === 'farm') {
    if (type === 'dog') {
      // Larger, more active dogs for bigger spaces
      if (breed.height?.metric) {
        const avgHeight = parseFloat(breed.height.metric.split(' - ')[0]) || 30;
        if (avgHeight > 50) score += 15; // Larger breeds
      }
      if (breed.bred_for?.toLowerCase().includes('working')) score += 10;
    }
  }

  // Time commitment scoring
  if (time === 'minimal') {
    if (type === 'dog') {
      if (breed.temperament?.toLowerCase().includes('independent')) score += 20;
      if (breed.energy_level <= 2) score += 15;
    } else {
      if (breed.energy_level <= 2) score += 20;
      if (breed.grooming <= 2) score += 10;
    }
  } else if (time === 'lots' || time === 'all-day') {
    if (type === 'dog') {
      if (breed.temperament?.toLowerCase().includes('active')) score += 15;
      if (breed.energy_level >= 4) score += 15;
    } else {
      if (breed.energy_level >= 4) score += 15;
      if (breed.social_needs >= 4) score += 10;
    }
  }

  // Size preference scoring
  if (size === 'small') {
    if (type === 'dog' && breed.weight?.metric) {
      const avgWeight = parseFloat(breed.weight.metric.split(' - ')[0]) || 25;
      if (avgWeight < 25) score += 25;
    } else if (type === 'cat') {
      // Most cats are naturally small-medium
      score += 15;
    }
  } else if (size === 'large') {
    if (type === 'dog' && breed.weight?.metric) {
      const avgWeight = parseFloat(breed.weight.metric.split(' - ')[0]) || 25;
      if (avgWeight > 27) score += 25;
    }
  }

  // Energy level scoring
  const energyMap = { low: 1, moderate: 3, high: 4, 'very-high': 5 };
  const userEnergyLevel = energyMap[energy] || 3;
  
  if (type === 'dog') {
    // Estimate energy level from temperament for dogs
    let breedEnergy = 3;
    const temp = breed.temperament?.toLowerCase() || '';
    if (temp.includes('energetic') || temp.includes('active')) breedEnergy = 5;
    else if (temp.includes('calm') || temp.includes('gentle')) breedEnergy = 2;
    else if (temp.includes('playful')) breedEnergy = 4;
    
    const energyDiff = Math.abs(userEnergyLevel - breedEnergy);
    score += Math.max(0, 20 - (energyDiff * 5));
  } else {
    // Cats have energy_level in API
    const breedEnergy = breed.energy_level || 3;
    const energyDiff = Math.abs(userEnergyLevel - breedEnergy);
    score += Math.max(0, 20 - (energyDiff * 5));
  }

  return Math.max(0, Math.min(100, score));
};

/**
 * Get top breed matches based on user preferences
 */
export const getBreedMatches = async (userPreferences, limit = 5) => {
  try {
    const [dogBreeds, catBreeds] = await Promise.all([
      fetchDogBreeds(),
      fetchCatBreeds()
    ]);

    // Score all breeds
    const scoredDogs = dogBreeds.map(breed => ({
      ...breed,
      type: 'dog',
      score: scoreBreed(breed, userPreferences, 'dog'),
      id: breed.id
    }));

    const scoredCats = catBreeds.map(breed => ({
      ...breed,
      type: 'cat',
      score: scoreBreed(breed, userPreferences, 'cat'),
      id: breed.id
    }));

    // Combine and sort by score
    const allBreeds = [...scoredDogs, ...scoredCats];
    
    // Separate common and rare breeds
    const commonBreeds = allBreeds.filter(breed => isCommonBreed(breed.name, breed.type));
    const rareBreeds = allBreeds.filter(breed => !isCommonBreed(breed.name, breed.type));
    
    // Prioritize common breeds but include some rare ones for variety
    const topCommon = commonBreeds
      .filter(breed => breed.score > 25)
      .sort((a, b) => b.score - a.score)
      .slice(0, Math.max(3, limit - 1)); // At least 3 common breeds
      
    const topRare = rareBreeds
      .filter(breed => breed.score > 40) // Higher threshold for rare breeds
      .sort((a, b) => b.score - a.score)
      .slice(0, limit - topCommon.length);
    
    const topBreeds = [...topCommon, ...topRare]
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);

    // Fetch images for top breeds
    const breedsWithImages = await Promise.all(
      topBreeds.map(async (breed) => {
        const image = await fetchBreedImage(breed.id, breed.type);
        const fallbackImage = demoImages[breed.id] || `https://via.placeholder.com/300x200/e5e7eb/6b7280?text=${encodeURIComponent(breed.name)}`;
        return {
          ...breed,
          image: image || fallbackImage
        };
      })
    );

    return breedsWithImages;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting breed matches:', error);
    return [];
  }
};

/**
 * Format breed information for display
 */
export const formatBreedInfo = (breed) => {
  const isdog = breed.type === 'dog';
  
  return {
    id: breed.id,
    name: breed.name,
    type: breed.type,
    image: breed.image,
    score: breed.score,
    
    // Basic info
    lifeSpan: breed.life_span,
    weight: isdog ? breed.weight?.metric : breed.weight?.metric,
    height: isdog ? breed.height?.metric : null,
    
    // Personality traits
    temperament: breed.temperament,
    bredFor: isdog ? breed.bred_for : null,
    origin: breed.origin,
    
    // Ratings (cats have more detailed ratings)
    energyLevel: breed.energy_level,
    affectionLevel: breed.affection_level,
    childFriendly: breed.child_friendly,
    dogFriendly: breed.dog_friendly,
    socialNeeds: breed.social_needs,
    grooming: breed.grooming,
    intelligence: breed.intelligence,
    
    // Description
    description: breed.description || `The ${breed.name} is known for being ${breed.temperament?.toLowerCase()}.`,
    
    // Additional info
    hairless: breed.hairless,
    hypoallergenic: breed.hypoallergenic,
    rare: breed.rare
  };
};